import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { DEFAULT_LIFECYCLE_CONFIG, type Invoice, type Timestamp } from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import { invoicesRepoFor } from '../lib/repositories/invoices-repo.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { logger } from '../lib/logger.js';
import { assembleCountdownLine, computeDaysUntilSuspension } from '../lib/past-due-countdown.js';

/**
 * Operator past-due warning banner (roadmap #9).
 *
 * A NON-BLOCKING amber strip shown at the very top of the dashboard ONLY when
 * the tenant is `past_due`. It warns the operator to pay BEFORE suspension (#8)
 * takes the queue offline. `past_due` keeps the queue fully functional
 * (`tenantCanTakeBookings('past_due') === true`) — this banner never gates
 * anything; it renders in-flow and pushes route content down, so it does not
 * overlap any route's own header (there is no shared shell reserving top space,
 * so a fixed bar would overlay each `<main>`; an in-flow block cannot).
 *
 * Reads only what /billing already reads — the tenant root doc (status) and the
 * tenant's invoices — and only subscribes to invoices AFTER confirming the
 * tenant is `past_due`, so the common (active/trial) case costs a single doc
 * read and no live listener.
 */
export function PastDueBanner() {
  const { t } = useI18n();
  const { user, claims } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  // Only owner/manager (and super-admin) may read the tenant's invoices per
  // infra/firestore.rules. Cashier/washer must NOT open a doomed
  // permission-denied listener — they still see the banner (title/body/CTA),
  // just without the day-countdown line (which needs the oldest invoice).
  const rolesClaim = claims?.['roles'];
  const tenantRole =
    tenantId && rolesClaim && typeof rolesClaim === 'object'
      ? (rolesClaim as Record<string, unknown>)[tenantId]
      : undefined;
  const canReadInvoices =
    tenantRole === 'owner' || tenantRole === 'manager' || Boolean(claims?.['superadmin']);

  // One-shot tenant status read (mirrors HomeRoute). We only need to know
  // whether the tenant is `past_due`; a live listener is unnecessary for a
  // dunning banner and would add a listener to every dashboard page.
  const [isPastDue, setIsPastDue] = useState<boolean>(false);
  useEffect(() => {
    if (!user || !tenantId) {
      setIsPastDue(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const tenant = await tenantsRepo.get(tenantId);
        if (!cancelled) setIsPastDue(tenant?.profile.status === 'past_due');
      } catch (e) {
        // On a transient read failure, stay silent rather than alarm the
        // operator with a warning we can't substantiate.
        logger.warn('PastDueBanner: tenant load failed', { error: String(e) });
        if (!cancelled) setIsPastDue(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, tenantId]);

  // Subscribe to invoices ONLY once we know the tenant is past_due (paired
  // unsubscribe via useTrackedSnapshot — rule 8). Null subscribe = no listener.
  const invoicesSubscribe = useMemo(() => {
    if (!tenantId || !isPastDue || !canReadInvoices) return null;
    const repo = invoicesRepoFor(tenantId);
    return (cb: (vs: Invoice[]) => void) => repo.listSnapshot(cb);
  }, [tenantId, isPastDue, canReadInvoices]);
  const { data: invoices } = useTrackedSnapshot<Invoice[]>(
    ['past-due-invoices', tenantId],
    invoicesSubscribe,
  );

  // Oldest unpaid (open | past_due) invoice by dueDate — the invoice the
  // server's lifecycle sweep measures the suspension window against.
  const oldestUnpaid = useMemo(() => {
    if (!invoices) return null;
    let best: { dueMs: number; paymentLink: string | undefined } | null = null;
    for (const inv of invoices) {
      if (inv.status !== 'open' && inv.status !== 'past_due') continue;
      const dueMs = timestampToMs(inv.dueDate);
      if (dueMs <= 0) continue;
      if (best === null || dueMs < best.dueMs) {
        best = { dueMs, paymentLink: inv.paymentLink };
      }
    }
    return best;
  }, [invoices]);

  // Per-session, per-tenant dismiss. Reappears next session while still
  // past_due (the state hasn't been resolved — only quietened for now).
  const dismissKey = tenantId ? `kilat.pastDueDismissed.${tenantId}` : null;
  const [dismissed, setDismissed] = useState<boolean>(false);
  useEffect(() => {
    if (!dismissKey) return;
    try {
      setDismissed(window.sessionStorage.getItem(dismissKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  if (!isPastDue || dismissed) return null;

  const countdown = oldestUnpaid
    ? computeDaysUntilSuspension(
        oldestUnpaid.dueMs,
        Date.now(),
        DEFAULT_LIFECYCLE_CONFIG.suspendAfterDays,
      )
    : null;

  // Copy assembly lives in the pure, unit-tested `assembleCountdownLine`
  // (past-due-countdown.ts) so the i18n var names are covered — see its test.
  const countdownLine = assembleCountdownLine(countdown, t, formatDate);

  function dismiss() {
    if (dismissKey) {
      try {
        window.sessionStorage.setItem(dismissKey, '1');
      } catch {
        /* swallow — dismiss is best-effort */
      }
    }
    setDismissed(true);
  }

  return (
    <div role="status" data-shell-banner style={stripStyle}>
      <div style={textColStyle}>
        <p style={eyebrowStyle}>{t('dashboard.pastDue.eyebrow')}</p>
        <p style={titleStyle}>{t('dashboard.pastDue.title')}</p>
        <p style={bodyStyle}>{t('dashboard.pastDue.body')}</p>
        {countdownLine ? <p style={countdownStyle}>{countdownLine}</p> : null}
      </div>
      <div style={actionColStyle}>
        <Link to="/billing" style={ctaStyle}>
          {t('dashboard.pastDue.cta')}
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('dashboard.pastDue.dismiss')}
          title={t('dashboard.pastDue.dismiss')}
          style={dismissStyle}
        >
          {'×'}
        </button>
      </div>
    </div>
  );
}

function timestampToMs(ts: Timestamp | undefined): number {
  if (!ts) return 0;
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

// Amber "warning" tone (NOT the red blocking tone of SuspendedLock). Amber
// matches the pending/open invoice pill in routes/billing.tsx; red is reserved
// for the hard suspended lock. Full-width, in-flow strip.
const AMBER_TEXT = '#b45309';

const stripStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  width: '100%',
  boxSizing: 'border-box',
  padding: 'clamp(10px, 2vw, 16px) clamp(14px, 3vw, 24px)',
  background: 'rgba(245,158,11,0.12)',
  borderBottom: '1px solid rgba(245,158,11,0.35)',
  color: AMBER_TEXT,
};

const textColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
  flex: '1 1 260px',
};

const eyebrowStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: AMBER_TEXT,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
  color: AMBER_TEXT,
};

const bodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: AMBER_TEXT,
  opacity: 0.9,
  overflowWrap: 'anywhere',
};

const countdownStyle: React.CSSProperties = {
  margin: '2px 0 0 0',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  color: AMBER_TEXT,
  overflowWrap: 'anywhere',
};

const actionColStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flex: '0 0 auto',
};

const ctaStyle: React.CSSProperties = {
  display: 'inline-block',
  background: AMBER_TEXT,
  color: '#fff',
  padding: '10px 18px',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const dismissStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'var(--touch-target, 32px)',
  height: 'var(--touch-target, 32px)',
  minWidth: 32,
  minHeight: 32,
  border: 'none',
  background: 'transparent',
  color: AMBER_TEXT,
  fontSize: '20px',
  lineHeight: 1,
  cursor: 'pointer',
  borderRadius: 'var(--radius-pill)',
};
